import type {
  ContinueWatching,
  Genre,
  Profile,
  ExamHistory,
  ExamKind,
  ExamQuestion,
  ExamResult,
  QuizQuestion,
  ReviewCard,
  SavedWord,
  StudyToday,
  TopikLevel,
  Video,
  VideoScript,
  Word,
} from '../lib/types'
import { curationDistance } from '../lib/level'
import { EXAM_HISTORY_ATTEMPTS, EXAM_SIZE, MASTER_STREAK, REVIEW_INTERVALS } from '../lib/types'
import type { Repository } from './repository'
import {
  LINE_WORDS,
  QUIZ_QUESTIONS,
  SEED_SAVED_WORDS,
  SUBTITLE_LINES,
  VIDEOS,
  WORDS,
} from './seed'

const PROFILE_KEY = 'ktopik.profile'
const VOCAB_KEY = 'ktopik.vocab'
const PROGRESS_KEY = 'ktopik.progress'
const DAYS_KEY = 'ktopik.studydays'
const EXAMS_KEY = 'ktopik.exams'

/** Demo mode 의 점검 회차 기록 — 약점 영역을 계산하는 재료다 */
type ExamHistoryRow = { at: string; byKind: ExamResult['byKind'] }

/** 로컬 저장분에만 있는 복습 필드. 시드 타입을 더럽히지 않으려고 여기서만 좁힌다. */
type LocalReview = { reviewDue?: string; reviewStreak?: number; reviewedAt?: string; masteredAt?: string }
const local = (v: unknown): LocalReview => v as LocalReview
/** 예정일이 없으면 아직 한 번도 복습하지 않은 것 — 바로 대상이 된다. */
function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}
const dueNow = (due?: string) => !due || due <= new Date().toLocaleDateString('en-CA')

/**
 * 저장분에 자막 끝 시각과 시험용 예문을 붙인다.
 *
 * localStorage 에 이미 들어있는 옛 저장분에는 두 값이 없다. 저장 시점에 복사해 두는
 * 대신 매번 시드에서 다시 찾는다 — Supabase 쪽도 join 으로 그때그때 가져오므로
 * 예문을 고치면 양쪽 다 바로 반영된다.
 */
function enrich(v: SavedWord): SavedWord {
  const line = SUBTITLE_LINES.find((l) => l.id === v.lineId)
  const word = WORDS.find((w) => w.id === v.wordId)
  return { ...v, endSec: line?.endSec ?? v.endSec ?? null, example: word?.example ?? null }
}

const DEFAULT_PROFILE: Profile = {
  id: 'local-user',
  displayName: 'Hana',
  topikLevel: 3,
  preferredGenres: [],
  // 로컬 시드 모드에는 쓸 DB 가 없으므로 어드민 개념이 없다.
  isAdmin: false,
  meaningLang: null,
  subtitleMode: null,
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* 사파리 프라이빗 모드 등 — 저장 실패는 무시하고 메모리 상태만 유지 */
  }
}

/** 네트워크 없이 즉시 응답. 화면 코드가 async 를 전제로 하므로 Promise 로 감싼다. */
export class LocalRepository implements Repository {
  readonly mode = 'local' as const

  async getProfile(): Promise<Profile> {
    return read(PROFILE_KEY, DEFAULT_PROFILE)
  }

  async updateProfile(patch: Partial<Profile>): Promise<Profile> {
    const next = { ...(await this.getProfile()), ...patch }
    write(PROFILE_KEY, next)
    return next
  }

  async getQuizQuestions(): Promise<QuizQuestion[]> {
    return QUIZ_QUESTIONS
  }

  /**
   * Demo mode 에는 리포트 문구가 없다.
   *
   * 9구간 × 15문장 × 2개 언어를 시드에 복사해 두면 DB 와 두 벌이 되고, 기획자가 문구를
   * 고칠 때마다 어긋난다. null 을 돌려주면 결과 화면이 급수 요약으로 되돌아간다.
   */
  async getLevelReport(): Promise<null> {
    return null
  }

  async saveQuizAttempt(_answers: Array<number | null>, estimatedLevel: TopikLevel): Promise<void> {
    await this.updateProfile({ topikLevel: estimatedLevel })
  }

  async listCuratedVideos(level: TopikLevel, genre: Genre | 'all'): Promise<Video[]> {
    // 급수로 거르지 않는다 — 전부 보여주되 내 급수에 가까운 순으로 올린다.
    return VIDEOS.filter(
      (v) => v.status === 'published' && (genre === 'all' || v.genre === genre),
    ).sort((a, b) => curationDistance(a.topikLevel, level) - curationDistance(b.topikLevel, level))
  }

  async getVideoScript(videoId: string): Promise<VideoScript | null> {
    const video = VIDEOS.find((v) => v.id === videoId || v.slug === videoId)
    if (!video) return null

    const lines = SUBTITLE_LINES.filter((l) => l.videoId === videoId).sort((a, b) => a.idx - b.idx)
    const lineIds = new Set(lines.map((l) => l.id))

    const tokens: Record<number, Record<string, number>> = {}
    const words: Record<number, Word> = {}
    for (const lw of LINE_WORDS) {
      if (!lineIds.has(lw.lineId)) continue
      ;(tokens[lw.lineId] ??= {})[lw.token] = lw.wordId
      const word = WORDS.find((w) => w.id === lw.wordId)
      if (word) words[word.id] = word
    }

    return { video, lines, words, tokens }
  }

  async getContinueWatching(): Promise<ContinueWatching | null> {
    const all = read<Record<string, { positionSec: number; at: number }>>(PROGRESS_KEY, {})
    const recent = Object.entries(all).sort((a, b) => b[1].at - a[1].at)
    for (const [videoId, p] of recent) {
      const video = VIDEOS.find((v) => v.id === videoId)
      if (!video) continue
      const percent = video.durationSec > 0 ? (p.positionSec / video.durationSec) * 100 : 0
      if (percent >= 95) continue
      return { video, positionSec: p.positionSec, percent: Math.min(100, Math.round(percent)) }
    }
    return null
  }

  async saveProgress(videoId: string, positionSec: number): Promise<void> {
    const all = read<Record<string, { positionSec: number; at: number }>>(PROGRESS_KEY, {})
    all[videoId] = { positionSec, at: Date.now() }
    write(PROGRESS_KEY, all)
  }

  async listSavedWords(): Promise<SavedWord[]> {
    // 미암기를 먼저, 그 안에서 최신순 (Supabase 쪽 정렬과 맞춘다)
    return [...read(VOCAB_KEY, SEED_SAVED_WORDS)]
      .sort((a, b) => Number(a.mastered) - Number(b.mastered))
      .map(enrich)
  }

  async saveWord(
    input: Omit<SavedWord, 'id' | 'createdAt' | 'mastered'>,
  ): Promise<{ saved: SavedWord; duplicate: boolean }> {
    const list = await this.listSavedWords()
    const existing = list.find((v) => v.wordId === input.wordId)
    if (existing) return { saved: existing, duplicate: true }

    const saved: SavedWord = {
      ...input,
      mastered: false,
      id: `local-${input.wordId}-${list.length}`,
      createdAt: new Date().toISOString(),
    }
    write(VOCAB_KEY, [saved, ...list])
    return { saved, duplicate: false }
  }

  async deleteSavedWord(id: string): Promise<void> {
    const list = await this.listSavedWords()
    write(
      VOCAB_KEY,
      list.filter((v) => v.id !== id),
    )
  }

  async setWordMastered(id: string, mastered: boolean): Promise<void> {
    const list = await this.listSavedWords()
    write(
      VOCAB_KEY,
      list.map((v) =>
        v.id === id ? { ...v, mastered, masteredAt: mastered ? new Date().toISOString() : null } : v,
      ),
    )
  }

  /* --- 실력 점검 ------------------------------------------------------------
     Demo mode 는 시드가 작아 어휘·빈칸 문항만 만든다. 흐름은 같다. */

  async getExamQuestions(level: TopikLevel, count = EXAM_SIZE): Promise<ExamQuestion[]> {
    const pool = WORDS.filter((w) => Math.abs(w.topikLevel - level) <= 2)
    const qs: ExamQuestion[] = []
    for (const w of shuffle([...pool]).slice(0, count)) {
      const wrong = shuffle(pool.filter((x) => x.id !== w.id))
        .slice(0, 3)
        .map((x) => x.meaningEn)
      if (wrong.length < 3) continue
      const options = shuffle([w.meaningEn, ...wrong])
      qs.push({
        id: `m:${w.lemma}`,
        kind: 'meaning',
        prompt: '다음 단어의 뜻으로 알맞은 것을 고르십시오.',
        passage: w.lemma,
        options,
        answerIdx: options.indexOf(w.meaningEn),
        level: w.topikLevel,
      })
    }
    return qs.slice(0, count)
  }

  async saveExamResult(result: ExamResult): Promise<void> {
    // Demo mode 도 약점 영역을 볼 수 있게 회차를 남긴다 (기기 안에만).
    const prev = read<ExamHistoryRow[]>(EXAMS_KEY, [])
    write(EXAMS_KEY, [{ at: new Date().toISOString(), byKind: result.byKind }, ...prev].slice(0, 50))
    await this.markStudyDay()
  }

  async getExamHistory(limit = EXAM_HISTORY_ATTEMPTS): Promise<ExamHistory> {
    const rows = read<ExamHistoryRow[]>(EXAMS_KEY, []).slice(0, limit)
    const acc = new Map<ExamKind, { correct: number; total: number }>()
    for (const r of rows) {
      for (const b of r.byKind) {
        const cur = acc.get(b.kind) ?? { correct: 0, total: 0 }
        cur.correct += b.correct
        cur.total += b.total
        acc.set(b.kind, cur)
      }
    }
    return {
      attempts: rows.length,
      lastAt: rows[0]?.at ?? null,
      areas: [...acc].map(([kind, v]) => ({ kind, ...v })),
    }
  }

  /* --- 복습 (간격 반복) ----------------------------------------------------
     Demo mode 도 같은 흐름을 볼 수 있게 시드의 LINE_WORDS 로 문제를 만든다. */

  async listDueReviews(limit = 10): Promise<ReviewCard[]> {
    const list = (await this.listSavedWords()).filter(
      (v) => !v.mastered && dueNow(local(v).reviewDue),
    )
    const cards: ReviewCard[] = []
    for (const v of list.slice(0, limit)) {
      const line = SUBTITLE_LINES.find((l) => l.id === v.lineId)
      if (!line) continue
      const mapped = LINE_WORDS.find((lw) => lw.lineId === v.lineId && lw.wordId === v.wordId)
      const token = mapped?.token ?? (line.text.includes(v.lemma) ? v.lemma : null)
      if (!token) continue

      const others = WORDS.filter((w) => w.id !== v.wordId && w.lemma !== v.lemma)
        .sort((a, b) => Math.abs(a.topikLevel - v.topikLevel) - Math.abs(b.topikLevel - v.topikLevel))
        .slice(0, 12)
        .sort(() => Math.random() - 0.5)
      const options = [v.lemma, ...others.slice(0, 3).map((w) => w.lemma)].sort(
        () => Math.random() - 0.5,
      )

      cards.push({
        savedWordId: v.id,
        wordId: v.wordId,
        lemma: v.lemma,
        meaningEn: v.meaningEn,
        meanings: v.meanings,
        topikLevel: v.topikLevel,
        sentence: line.text,
        token,
        startSec: v.startSec,
        endSec: line.endSec,
        videoId: v.videoId,
        youtubeId: v.youtubeId,
        options,
        answerIdx: options.indexOf(v.lemma),
      })
    }
    return cards
  }

  async submitReview(savedWordId: string, correct: boolean): Promise<void> {
    const list = await this.listSavedWords()
    const now = new Date().toISOString()
    write(
      VOCAB_KEY,
      list.map((v) => {
        if (v.id !== savedWordId) return v
        const streak = correct ? (local(v).reviewStreak ?? 0) + 1 : 0
        const days = correct
          ? REVIEW_INTERVALS[Math.min(streak - 1, REVIEW_INTERVALS.length - 1)]
          : 1
        const due = new Date()
        due.setDate(due.getDate() + days)
        const mastered = correct && streak >= MASTER_STREAK
        return {
          ...v,
          reviewStreak: streak,
          reviewDue: due.toLocaleDateString('en-CA'),
          reviewedAt: now,
          ...(mastered ? { mastered: true, masteredAt: now } : {}),
        }
      }),
    )
    await this.markStudyDay()
  }

  /* --- 오늘의 과제 --------------------------------------------------------
     Demo mode 도 화면이 비어 보이지 않게 localStorage 로 같은 모양을 만든다. */

  async markStudyDay(): Promise<void> {
    const day = new Date().toLocaleDateString('en-CA')
    const days = read<string[]>(DAYS_KEY, [])
    if (!days.includes(day)) write(DAYS_KEY, [day, ...days].slice(0, 400))
  }

  async getStudyToday(): Promise<StudyToday> {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const since = start.getTime()
    const list = await this.listSavedWords()
    const days = read<string[]>(DAYS_KEY, [])
    const todayStr = new Date().toLocaleDateString('en-CA')

    const set = new Set(days)
    const cursor = new Date(`${todayStr}T00:00:00`)
    if (!set.has(todayStr)) cursor.setDate(cursor.getDate() - 1)
    let streak = 0
    while (set.has(cursor.toLocaleDateString('en-CA'))) {
      streak += 1
      cursor.setDate(cursor.getDate() - 1)
    }

    const at = (v: string | null | undefined) => (v ? new Date(v).getTime() : 0)
    return {
      streak,
      watched: Object.values(
        read<Record<string, { positionSec: number; at: number }>>(PROGRESS_KEY, {}),
      ).filter((p) => p.at >= since).length,
      saved: list.filter((v) => at(v.createdAt) >= since).length,
      mastered: list.filter((v) => at((v as { masteredAt?: string }).masteredAt) >= since).length,
      reviewed: list.filter((v) => at(local(v).reviewedAt) >= since).length,
      due: list.filter((v) => !v.mastered && dueNow(local(v).reviewDue)).length,
    }
  }
}
